import React, { useEffect, useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { Activity, Users, Eye, BarChart3, TrendingUp, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";

/**
 * Phase 8A — Admin Analytics Dashboard.
 *
 * Renders 4 KPI cards + daily line chart (svg, no vendor) + top pages
 * table + 4 funnels (conversion, onboarding, email, retention).
 * A small live badge subscribes to `/api/admin/analytics/realtime` SSE.
 */

const RANGES = [
    { key: 7, label: "7d" },
    { key: 30, label: "30d" },
    { key: 90, label: "90d" },
];

function Card({ label, value, testid, hint = "", accent = "primary" }) {
    return (
        <div
            data-testid={testid}
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5"
        >
            <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-1">{label}</div>
            <div className={`text-2xl md:text-3xl font-semibold text-${accent === "primary" ? "text-main" : accent}`}>
                {value}
            </div>
            {hint && <div className="text-[11px] text-text-secondary mt-2">{hint}</div>}
        </div>
    );
}

function DailyChart({ daily }) {
    const w = 900;
    const h = 220;
    const p = 24;
    const points = daily || [];
    if (!points.length) {
        return <div className="text-sm text-text-secondary py-8 text-center">No pageviews yet in this window.</div>;
    }
    const maxY = Math.max(1, ...points.map((r) => r.pageviews || 0));
    const stepX = points.length > 1 ? (w - p * 2) / (points.length - 1) : 0;
    const line = points.map((r, i) => {
        const x = p + i * stepX;
        const y = h - p - (r.pageviews / maxY) * (h - p * 2);
        return `${i === 0 ? "M" : "L"} ${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");

    return (
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-56" data-testid="analytics-daily-chart">
            <defs>
                <linearGradient id="fill1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
                </linearGradient>
            </defs>
            {/* Baseline */}
            <line x1={p} y1={h - p} x2={w - p} y2={h - p} stroke="#1A1A1A" strokeWidth="1" />
            {/* Area */}
            <path d={`${line} L ${w - p},${h - p} L ${p},${h - p} Z`} fill="url(#fill1)" />
            {/* Line */}
            <path d={line} stroke="#FFFFFF" strokeWidth="2" fill="none" />
            {/* Points */}
            {points.map((r, i) => {
                const x = p + i * stepX;
                const y = h - p - (r.pageviews / maxY) * (h - p * 2);
                return <circle key={i} cx={x} cy={y} r="3" fill="#CCCCCC" />;
            })}
        </svg>
    );
}

function FunnelSteps({ steps, testid }) {
    const first = steps[0]?.value ?? 0;
    return (
        <div className="space-y-2" data-testid={testid}>
            {steps.map((s, i) => {
                const pct = first > 0 ? Math.round((s.value / first) * 100) : 0;
                const width = Math.max(4, pct);
                return (
                    <div key={s.key}>
                        <div className="flex items-center justify-between text-xs text-text-secondary mb-1">
                            <span>{s.label}</span>
                            <span className="tabular-nums text-text-main font-medium">
                                {s.value.toLocaleString()} <span className="text-text-secondary">({pct}%)</span>
                            </span>
                        </div>
                        <div className="h-2 rounded-full bg-[var(--color-panel-ai)] overflow-hidden">
                            <div
                                data-testid={`funnel-step-${s.key}-bar`}
                                className="h-full bg-white hover:bg-[#EDEDED]"
                                style={{ width: `${width}%` }}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function AdminAnalytics() {
    const { user } = useAuth();
    const [days, setDays] = useState(30);
    const [overview, setOverview] = useState(null);
    const [topPages, setTopPages] = useState([]);
    const [conv, setConv] = useState(null);
    const [onb, setOnb] = useState(null);
    const [emailF, setEmailF] = useState(null);
    const [retention, setRetention] = useState(null);
    const [realtime, setRealtime] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [o, tp, c, ob, e, r] = await Promise.all([
                apiFetch(`/admin/analytics/overview?days=${days}`),
                apiFetch(`/admin/analytics/top-pages?days=${days}&limit=20`),
                apiFetch(`/admin/analytics/funnels/conversion?days=${days}`),
                apiFetch(`/admin/analytics/funnels/onboarding?days=${days}`),
                apiFetch(`/admin/analytics/funnels/email?days=${days}`),
                apiFetch(`/admin/analytics/funnels/retention`),
            ]);
            setOverview(o); setTopPages(tp.pages || []); setConv(c); setOnb(ob); setEmailF(e); setRetention(r);
        } catch (e) {
            toast.error(e.message || "Failed to load analytics");
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => { load(); }, [load]);

    const emailByType = useMemo(() => emailF?.by_type || [], [emailF]);

    // SSE realtime subscription (30s). Cookie auth carries admin session.
    useEffect(() => {
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        const url = `${base}/api/admin/analytics/realtime?interval=30`;
        const es = new EventSource(url, { withCredentials: true });
        es.onmessage = (evt) => {
            try {
                const payload = JSON.parse(evt.data);
                setRealtime(payload);
            } catch {}
        };
        es.onerror = () => { /* silent — SSE reconnects automatically */ };
        return () => es.close();
    }, []);

    if (!isAdminRole(user?.role)) {
        return (
            <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex items-center justify-center p-8">
                <div className="text-center">
                    <h1 className="text-2xl font-semibold mb-2">Admin only</h1>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0">
                <Header />
                <main className="flex-1 p-6 md:p-8 max-w-6xl mx-auto w-full" data-testid="analytics-dashboard">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] mb-1">
                                Self-hosted analytics
                            </div>
                            <h1 className="text-3xl md:text-4xl font-semibold text-text-main">Dashboard</h1>
                            {realtime && (
                                <div className="mt-2 flex items-center gap-2 text-xs" data-testid="analytics-realtime">
                                    <span className="inline-flex h-2 w-2 rounded-full bg-[var(--color-success)] animate-pulse" />
                                    <span className="text-text-secondary">
                                        <span className="text-text-main font-medium">{realtime.active_sessions_last_5min}</span>{" "}
                                        active now · {realtime.pageviews_last_hour} pageviews last hour
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {RANGES.map((r) => (
                                <button
                                    key={r.key}
                                    data-testid={`analytics-range-${r.label}`}
                                    onClick={() => setDays(r.key)}
                                    className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full transition-colors ${
                                        days === r.key
                                            ? "bg-white hover:bg-[#EDEDED] text-black"
                                            : "border border-[var(--color-border)] text-text-secondary hover:text-text-main"
                                    }`}
                                >
                                    {r.label}
                                </button>
                            ))}
                            <button
                                onClick={load}
                                data-testid="analytics-refresh"
                                className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-3 py-1.5 flex items-center gap-1"
                            >
                                <RefreshCw className="h-3 w-3" /> Refresh
                            </button>
                        </div>
                    </div>

                    {loading || !overview ? (
                        <div className="flex items-center justify-center py-16">
                            <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {/* KPI cards */}
                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                                <Card label="Total pageviews" value={overview.total_pageviews.toLocaleString()} testid="kpi-total-pageviews" hint={`Last ${days} days`} />
                                <Card label="Unique visitors" value={overview.unique_visitors_sum.toLocaleString()} testid="kpi-unique-visitors" hint="Sum of daily distinct sessions" />
                                <Card label="Signups" value={conv?.steps[1]?.value?.toLocaleString?.() ?? 0} testid="kpi-signups" hint={`In last ${days} days`} />
                                <Card label="Paid users" value={conv?.steps[3]?.value?.toLocaleString?.() ?? 0} testid="kpi-paid" hint="Non-free plan" />
                            </div>

                            {/* Daily chart */}
                            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
                                <div className="flex items-center gap-2 mb-3">
                                    <Activity className="h-4 w-4 text-[var(--color-primary)]" />
                                    <h2 className="text-sm font-semibold text-text-main">Traffic daily</h2>
                                </div>
                                <DailyChart daily={overview.daily} />
                            </div>

                            {/* Top pages + Retention side-by-side */}
                            <div className="grid gap-6 lg:grid-cols-3">
                                <div className="lg:col-span-2 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6" data-testid="analytics-top-pages">
                                    <div className="flex items-center gap-2 mb-4">
                                        <BarChart3 className="h-4 w-4 text-[var(--color-primary)]" />
                                        <h2 className="text-sm font-semibold text-text-main">Top pages · last {days}d</h2>
                                    </div>
                                    {topPages.length === 0 ? (
                                        <p className="text-sm text-text-secondary">No data yet.</p>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead className="text-[10px] uppercase tracking-widest text-text-secondary">
                                                    <tr>
                                                        <th className="text-left py-2">URL</th>
                                                        <th className="text-right py-2">Pageviews</th>
                                                        <th className="text-right py-2">Uniques</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {topPages.map((p, i) => (
                                                        <tr key={i} className="border-t border-[var(--color-border)]/60">
                                                            <td className="py-2 text-text-main truncate max-w-xs">{p.url}</td>
                                                            <td className="py-2 text-right tabular-nums">{p.pageviews.toLocaleString()}</td>
                                                            <td className="py-2 text-right tabular-nums text-text-secondary">{p.unique_visitors.toLocaleString()}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                                <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6" data-testid="analytics-funnel-retention">
                                    <div className="flex items-center gap-2 mb-4">
                                        <TrendingUp className="h-4 w-4 text-[var(--color-primary)]" />
                                        <h2 className="text-sm font-semibold text-text-main">Retention</h2>
                                    </div>
                                    {retention && (
                                        <div className="space-y-3">
                                            {["D1", "D7", "D30"].map((k) => (
                                                <div key={k} className="flex items-center justify-between">
                                                    <span className="text-xs text-text-secondary">{k} return rate</span>
                                                    <span className="text-sm font-medium text-text-main tabular-nums">
                                                        {retention[k].returned}/{retention[k].cohort} · {(retention[k].rate * 100).toFixed(1)}%
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Funnels */}
                            <div className="grid gap-6 lg:grid-cols-2">
                                <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
                                    <h2 className="text-sm font-semibold text-text-main mb-4">Conversion funnel (visitors → paid)</h2>
                                    <FunnelSteps steps={conv?.steps || []} testid="analytics-funnel-conversion" />
                                </div>
                                <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
                                    <h2 className="text-sm font-semibold text-text-main mb-4">Onboarding funnel</h2>
                                    <FunnelSteps steps={onb?.steps || []} testid="analytics-funnel-onboarding" />
                                </div>
                            </div>

                            {/* Email drip breakdown */}
                            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6" data-testid="analytics-funnel-email">
                                <h2 className="text-sm font-semibold text-text-main mb-4">Email drip (last {days}d)</h2>
                                {emailByType.length === 0 ? (
                                    <p className="text-sm text-text-secondary">No drip records in this window.</p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead className="text-[10px] uppercase tracking-widest text-text-secondary">
                                                <tr>
                                                    <th className="text-left py-2">Type</th>
                                                    <th className="text-right py-2">Sent</th>
                                                    <th className="text-right py-2">Scheduled</th>
                                                    <th className="text-right py-2">Cancelled</th>
                                                    <th className="text-right py-2">Skipped (unsub)</th>
                                                    <th className="text-right py-2">Skipped (consent)</th>
                                                    <th className="text-right py-2">Failed</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {emailByType.map((r, i) => (
                                                    <tr key={i} className="border-t border-[var(--color-border)]/60">
                                                        <td className="py-2 text-text-main">{r._id}</td>
                                                        <td className="py-2 text-right tabular-nums">{r.sent}</td>
                                                        <td className="py-2 text-right tabular-nums text-text-secondary">{r.scheduled}</td>
                                                        <td className="py-2 text-right tabular-nums text-text-secondary">{r.cancelled}</td>
                                                        <td className="py-2 text-right tabular-nums text-text-secondary">{r.skipped_unsub}</td>
                                                        <td className="py-2 text-right tabular-nums text-text-secondary">{r.skipped_consent}</td>
                                                        <td className="py-2 text-right tabular-nums text-[var(--color-alert)]">{r.failed}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
